// Settings.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import {
    FaSun,
    FaMoon,
    FaPalette,
    FaGlobe,
    FaCheckCircle
} from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

function Settings() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('settings');
    const [theme, setTheme] = useState('auto');

    // Load theme from localStorage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'auto';
        setTheme(savedTheme);
        applyTheme(savedTheme);
    }, []);

    // Apply theme to document
    const applyTheme = (themeMode) => {
        if (themeMode === 'dark') {
            document.documentElement.classList.add('dark');
        } else if (themeMode === 'light') {
            document.documentElement.classList.remove('dark');
        } else if (themeMode === 'auto') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (prefersDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
        }
    };

    // Handle theme change - auto save
    const handleThemeChange = (e) => {
        const newTheme = e.target.value;
        setTheme(newTheme);
        applyTheme(newTheme);
        localStorage.setItem('theme', newTheme);

        const labels = {
            'auto': 'Auto',
            'light': 'Light',
            'dark': 'Dark'
        };
    };

    // Get theme icon
    const getThemeIcon = (themeValue) => {
        const icons = {
            'auto': <FaGlobe className="text-blue-500 text-xl" />,
            'light': <FaSun className="text-yellow-500 text-xl" />,
            'dark': <FaMoon className="text-purple-600 text-xl" />
        };
        return icons[themeValue] || icons.auto;
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Theme Settings</h1>
                            <p className="text-gray-600 text-sm">Changes are saved automatically</p>
                        </div>
                    </div>

                    {/* Theme Settings Card */}
                    <div className="max-w-2xl">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                    <FaPalette className="text-purple-600" />
                                    <span className="font-semibold text-gray-900">Appearance</span>
                                </div>
                            </div>

                            <div className="p-6">
                                {/* Theme Options */}
                                <div className="space-y-3">
                                    {/* Auto Option */}
                                    <div
                                        onClick={() => handleThemeChange({ target: { value: 'auto' } })}
                                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${theme === 'auto'
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'auto' ? 'bg-purple-100' : 'bg-gray-100'
                                                }`}>
                                                <FaGlobe className={`text-xl ${theme === 'auto' ? 'text-purple-600' : 'text-gray-400'
                                                    }`} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">Auto</p>
                                                <p className="text-xs text-gray-500">Follow system preference</p>
                                            </div>
                                        </div>
                                        {theme === 'auto' && (
                                            <FaCheckCircle className="text-purple-600 text-xl" />
                                        )}
                                    </div>

                                    {/* Light Option */}
                                    <div
                                        onClick={() => handleThemeChange({ target: { value: 'light' } })}
                                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${theme === 'light'
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'light' ? 'bg-purple-100' : 'bg-gray-100'
                                                }`}>
                                                <FaSun className={`text-xl ${theme === 'light' ? 'text-yellow-500' : 'text-gray-400'
                                                    }`} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">Light</p>
                                                <p className="text-xs text-gray-500">Bright and clean</p>
                                            </div>
                                        </div>
                                        {theme === 'light' && (
                                            <FaCheckCircle className="text-purple-600 text-xl" />
                                        )}
                                    </div>

                                    {/* Dark Option */}
                                    <div
                                        onClick={() => handleThemeChange({ target: { value: 'dark' } })}
                                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${theme === 'dark'
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-purple-100' : 'bg-gray-100'
                                                }`}>
                                                <FaMoon className={`text-xl ${theme === 'dark' ? 'text-purple-600' : 'text-gray-400'
                                                    }`} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">Dark</p>
                                                <p className="text-xs text-gray-500">Easy on the eyes</p>
                                            </div>
                                        </div>
                                        {theme === 'dark' && (
                                            <FaCheckCircle className="text-purple-600 text-xl" />
                                        )}
                                    </div>
                                </div>

                                {/* Current Status */}
                                <div className="mt-4 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                                    <span className="text-xs text-gray-500">Active Theme</span>
                                    <span className={`text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1.5 ${theme === 'dark'
                                            ? 'bg-gray-800 text-white'
                                            : theme === 'light'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                        {getThemeIcon(theme)}
                                        {theme.charAt(0).toUpperCase() + theme.slice(1)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="top-right" />
        </div>
    );
}

export default Settings;